import { useState } from 'react';

import { ChevronDownIcon } from '@heroicons/react/20/solid';

import { policyName } from '@/features/decisions/config/personaContext';
import {
  LEVER_LABELS,
  LEVER_ORDER,
  POLICY_NARRATIVES,
  type LeverState,
} from '@/features/decisions/config/policyNarrative';

interface Props {
  // Ordered experiment ids: reference (Incumbent) first, then comparisons.
  experimentIds: string[];
}

const BADGE_CLASS: Record<LeverState, string> = {
  off: 'bg-gray-100 text-gray-400',
  incumbent: 'bg-amber-400 text-amber-950',
  keystone: 'bg-indigo-900 text-white',
};

// "What changes between policies?" panel (issue #128). A persistent T/Q/S
// policy-lever header plus a retractable explanation, one column per policy.
export function PolicyChangesPanel({ experimentIds }: Props) {
  const [open, setOpen] = useState(false);

  const policies = experimentIds.flatMap((id) => {
    const n = POLICY_NARRATIVES[id];
    return n ? [{ id, ...n }] : [];
  });
  if (policies.length === 0) return null;

  const gridCols =
    policies.length >= 3 ? 'md:grid-cols-3' : policies.length === 2 ? 'md:grid-cols-2' : 'md:grid-cols-1';

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
      {/* Policy-lever header — serves as the chart's x-axis labels (Max review):
          canonical policy name on top, T/Q/S lever bars below. */}
      <div className={`grid grid-cols-1 divide-y divide-gray-100 ${gridCols} md:divide-x md:divide-y-0`}>
        {policies.map((p) => (
          <div key={p.id} className="flex flex-col items-center gap-2 px-4 py-4 text-center">
            <div>
              <div className="text-sm font-semibold text-gray-900">{policyName(p.id)}</div>
              {p.descriptor && <div className="text-xs text-gray-500">{p.descriptor}</div>}
            </div>
            <div className="flex items-center gap-1.5">
              {LEVER_ORDER.map((lever) => (
                <span
                  key={lever}
                  title={LEVER_LABELS[lever]}
                  className={`flex h-7 w-7 items-center justify-center rounded-md text-xs font-bold ${BADGE_CLASS[p.levers[lever]]}`}
                >
                  {lever}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Retractable explanation. */}
      <div className="border-t border-gray-200">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="flex w-full items-center gap-2 px-4 py-3 text-left text-sm font-semibold text-gray-900 hover:bg-gray-50"
        >
          <ChevronDownIcon className={`h-5 w-5 text-gray-400 transition-transform ${open ? '' : '-rotate-90'}`} />
          What changes between policies?
        </button>
        {open && (
          <div
            className={`grid grid-cols-1 divide-y divide-gray-100 border-t border-gray-100 ${gridCols} md:divide-x md:divide-y-0`}
          >
            {policies.map((p) => (
              <div key={p.id} className="px-4 py-4">
                <div className="flex items-center gap-2">
                  <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: p.dotColor }} />
                  <span className="text-sm font-semibold text-gray-900">
                    {policyName(p.id)}
                    {p.descriptor ? ` — ${p.descriptor}` : ''}
                  </span>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-gray-600">{p.blurb}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
